import { useEffect, useState } from 'react';
import { auditApi } from '../../lib/api';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Field, Select, TextInput } from '../../components/Field';
import { Modal } from '../../components/Modal';
import { ErrorBanner } from '../../components/ErrorBanner';
import { EditLogPanel } from './EditLogPanel';
import type { AuditEntry } from '../../lib/types';

const TABLES = ['ledgers', 'account_groups', 'company_features', 'users', 'branches', 'roles'];

export function ActivityPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [table, setTable] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [inspecting, setInspecting] = useState<AuditEntry | null>(null);

  function load() {
    auditApi
      .activity({ table: table || undefined, from: from || undefined, to: to || undefined })
      .then(({ items }) => setEntries(items))
      .catch(setError);
  }

  useEffect(load, [table, from, to]);

  const columns: DataTableColumn<AuditEntry>[] = [
    { key: 'when', header: 'When', render: (e) => new Date(e.occurredAt).toLocaleString('en-IN') },
    { key: 'table', header: 'Table', render: (e) => <span className="mono">{e.tableName}</span> },
    { key: 'record', header: 'Record', render: (e) => <span className="mono text-[11.5px] text-[var(--text-faint)]">{e.recordId}</span> },
    { key: 'action', header: 'Action', render: (e) => <span className="uppercase text-[11px]">{e.action}</span> },
    { key: 'actor', header: 'By', render: (e) => (e.actorMissing ? <span className="text-[var(--warn)]">system</span> : e.actorName ?? e.actorUserId ?? '—') },
  ];

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">Activity — tenant-wide edit log</h1>
        <p className="text-[11.5px] text-[var(--text-faint)]">
          Read-only, append-only. There is no way to edit, delete, or disable an entry (PRD §5.9).
        </p>
      </div>

      <div className="mb-3 flex items-end gap-3">
        <Field label="Table" htmlFor="act-table" inline>
          <Select id="act-table" value={table} onChange={(e) => setTable(e.target.value)}>
            <option value="">All</option>
            {TABLES.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="From" htmlFor="act-from" inline>
          <TextInput id="act-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="To" htmlFor="act-to" inline>
          <TextInput id="act-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>

      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

      <div className="min-h-0 flex-1">
        <DataTable
          columns={columns}
          rows={entries}
          getRowKey={(e) => e.id}
          onActivate={(e) => setInspecting(e)}
          emptyMessage="No activity recorded for this filter."
          className="h-full"
        />
      </div>
      <p className="mt-1 text-[11px] text-[var(--text-faint)]">Enter opens the field-level diff for the highlighted entry.</p>

      <Modal open={inspecting !== null} onClose={() => setInspecting(null)} title="Edit log entry" widthClassName="max-w-2xl">
        {inspecting && <EditLogPanel table={inspecting.tableName} recordId={inspecting.recordId} />}
      </Modal>
    </div>
  );
}
